import {
  type ImageMedia,
  type VideoMedia,
  type OPProductConnection,
} from "@root/tablescape/state";
import {
  type Filter,
  type CountryCode,
  type ProductCollectionSortKeys,
  type Maybe,
  type Image,
  type Collection,
  type LanguageCode,
} from "@shopify/hydrogen-react/storefront-api-types";
import { type HeadingTag } from "./typography";

export interface CollectionNavigationSettings {
  homeUrl: string;
}

export interface CollectionSectionSettings extends Record<string, unknown> {
  collectionHandle: string;
  collectionHero: {
    description: string;
    heroImage: Image;
    title: string;
    titleHeadingLevel: HeadingTag;
  };
  meta: {
    localization: { country: CountryCode; language: LanguageCode };
    pageType: string;
  };
  navigation: CollectionNavigationSettings;
  pagination: {
    pageSize: number;
  };
  settings: {
    version: string;
  };
  text: {
    appliedFiltersLabel: string;
    bestsellersLabel: string;
    breadcrumbsLabel: string;
    bundleValueText: string;
    clearAllLabel: string;
    emptyStateText: string;
    featuredLabel: string;
    filterButtonLabel: string;
    homeLabel: string;
    lessLabel: string;
    loadMoreLabel: string;
    loadingLabel: string;
    maxPriceAriaLabel: string;
    minPriceAriaLabel: string;
    moreLabel: string;
    newestLabel: string;
    nextLabel: string;
    prevLabel: string;
    priceHighToLowLabel: string;
    priceLowToHighLabel: string;
    quickAddButtonText: string;
    showProductsLabel: string;
    sortByLabel: string;
    viewLabel: string;
  };
}

export type CollectionContentCardConnection = {
  background: Maybe<{ value: string }>;
  ctaText: Maybe<{ value: string }>;
  ctaUrl: Maybe<{ value: string }>;
  description: Maybe<{ value: string }>;
  image: Maybe<{ reference: ImageMedia }>;
  mobileImage: Maybe<{ reference: ImageMedia }>;
  title: Maybe<{ value: string }>;
  videoControlColor: Maybe<{ value: string }>;
  videoMp4: Maybe<{ reference: VideoMedia }>;
  videoWebm: Maybe<{ reference: VideoMedia }>;
};

export type CollectionContentCard = {
  background?: string;
  ctaText?: string;
  ctaUrl?: string;
  description?: string;
  image?: ImageMedia;
  mobileImage?: ImageMedia;
  title?: string;
  videoControlColor?: string;
  videoMp4?: Partial<VideoMedia>;
  videoWebm?: Partial<VideoMedia>;
};

export type CollectionSortOption = {
  label: { value: string };
  reverse: { value: string };
  sortKey: { value: ProductCollectionSortKeys };
};

export type AdjacentCollectionsConnection = Maybe<{
  references: {
    nodes: {
      adjacentCollectionTitle: Maybe<{ value: string }>;
      handle: Collection["handle"];
      id: Collection["id"];
      onlineStoreUrl: Collection["onlineStoreUrl"];
      title: Collection["title"];
      titleOverride: Maybe<{ value: string }>;
    }[];
  };
}>;

export type CollectionConnection = {
  data?: {
    collection: {
      adjacentCollections: AdjacentCollectionsConnection;
      breadcrumbs: Maybe<{
        references: {
          nodes: {
            onlineStoreUrl: Collection["onlineStoreUrl"];
            title: Collection["title"];
            titleOverride: Maybe<{ value: string }>;
          }[];
        };
      }>;
      contentCards: Maybe<{
        references: {
          nodes: {
            contentCard: { reference: CollectionContentCardConnection };
            id: string;
            position: { value: string };
          }[];
        };
      }>;
      description: Collection["description"];
      descriptionOverride: Maybe<{ value: string }>;
      disableAdjacentCollectionTitleOverride: Maybe<{ value: string }>;
      handle: Collection["handle"];
      heroImage: Maybe<{ reference: { image: Image } }>;
      pageOnScroll: Maybe<{ value: string }>;
      pageSize: Maybe<{ value: string }>;
      products: {
        edges: { node: OPProductConnection }[];
        filters: Filter[];
        pageInfo: {
          endCursor: string;
          hasNextPage: boolean;
        };
      };
      quickAddEnabled: Maybe<{ value: string }>;
      seo: {
        title: Collection["seo"]["title"] | null | undefined;
      };
      sortOptions: Maybe<{
        references: {
          nodes: CollectionSortOption[];
        };
      }>;
      title: Collection["title"];
      titleOverride: Maybe<{ value: string }>;
      variantMatchFilter: Maybe<{ value: string }>;
    };
  };
};

export type PriceRange = {
  max: number | undefined;
  min: number;
};

export type SortOption = {
  name: string;
  value: {
    reverse: boolean;
    sortKey: ProductCollectionSortKeys;
  };
};

export const enum SortKeys {
  BEST_SELLING = "BEST_SELLING",
  COLLECTION_DEFAULT = "COLLECTION_DEFAULT",
  CREATED = "CREATED",
  PRICE = "PRICE",
}

export const enum CollectionContentCardType {
  VIDEO_CARD,
  IMAGE_CARD,
  IMAGE_TEXT_CARD,
  TEXT_CARD,
}
